// ── HormoneCharts.jsx ─────────────────────────────────────────────────────────
// Cycle and menopause transition charts for LH, FSH, E2, P4.
// Chart reference curves come from TestPage/hormoneData.
// Translated context text comes from TestPage/hormoneTranslations (EN/DE).

import React, { useState } from 'react';
import { Box, Chip, Divider, Typography, Tooltip, IconButton } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Check, Info, ChartNoAxesCombined, ChevronDown } from 'lucide-react';
import {
  ComposedChart, Area, Line, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ReferenceLine, ReferenceArea,
  ResponsiveContainer,
} from 'recharts';
import {
  tokens, MARKER_CHART, PHASE_COLORS,
  CYCLE_DATA, MENOPAUSE_CURVES, MENOPAUSE_STAGES,
  HRT_BAND, STATE_CHART_MODE,
  getMenopausePosition, stateHasHRT, stateHasContraception,
  P4_NMOL_PER_NGML,
} from './TestPage/hormoneData';
import { resources } from './TestPage/hormoneTranslations';

// ── Status color (mirrors HormoneDetailsFemale statusColor) ──────────────────

function statusColor(theme, status) {
  if (!status) return theme.palette.grey[400];
  if (status.toUpperCase() === 'NORMAL') return theme.palette.traffic.green;
  return theme.palette.traffic.red;
}

// ── Translation helper ────────────────────────────────────────────────────────

function ht(lang, ...path) {
  const base = resources[lang]?.hormone ?? resources.en?.hormone ?? {};
  return path.reduce((obj, key) => obj?.[key], base) ?? null;
}

// ── Alias lookup: marker_short (API) → chart marker ID ───────────────────────

// Exact marker_short values the API returns (lowercased for case-insensitive comparison).
// Ambiguous substring matching caused 'test' to match 'testf'/'teste2', so we use === here.
const MARKER_ALIASES = {
  LH:          ['lut'],
  FSH:         ['fol'],
  Prolactin:   ['prol', 'prl'],
  E2:          ['e2h', 'e2'],
  P4:          ['p4'],
  Testosterone:['test'],
  DHEAS:       ['dhea'],
  SHBG:        ['shbg'],
  BioT:        ['testf'],
  TE2ratio:    ['teste2'],
};

// Canonical display order — mirrors MARKER_IDS in hormoneData
const MARKER_GROUPS = [
  { labelKey: 'horm_section_glycoprotein', markers: ['LH', 'FSH', 'SHBG', 'Prolactin'] },
  { labelKey: 'horm_section_steroid',      markers: ['E2', 'P4', 'Testosterone', 'BioT', 'TE2ratio', 'DHEAS'] },
];

// Capitalized fallback marker_short values (matching translation keys like markername_Lut)
// used when a marker has no result in the selected visit
const MARKER_SHORT_FALLBACK = {
  LH: 'Lut', FSH: 'Fol', Prolactin: 'Prol',
  E2: 'E2h', P4: 'P4', Testosterone: 'Test',
  DHEAS: 'Dhea', SHBG: 'Shbg', BioT: 'Testf', TE2ratio: 'TestE2',
};

const MARKER_CHIPS = {
  LH:  ['mood', 'energy', 'libido'],
  FSH: ['fertility'],
  E2:  ['mood', 'energy', 'pain'],
  P4:  ['mood', 'sleep', 'pain'],
};

function findResult(hormoneResults, markerId) {
  const aliases = MARKER_ALIASES[markerId] ?? [];
  return hormoneResults.find(r => {
    const s = (r.marker_short ?? '').toLowerCase();
    return aliases.some(a => s === a);
  }) ?? null;
}

// ── Profile + status → hormoneState ──────────────────────────────────────────

function toHormoneState(profile, status) {
  if (profile === 'cycletracker') {
    return status === 'hormonal_birth_control' ? 'hormonal_contraception' : 'natural_cycle';
  }
  if (profile === 'perimenopause') return 'perimenopause';
  if (profile === 'postmenopause') return status === 'hrt' ? 'menopause_HRT' : 'menopause';
  return 'natural_cycle';
}

// ── Static chart label strings (axes / tooltips) ─────────────────────────────

const CHART_COPY = {
  dayLabel:      'Day',
  median:        'Median',
  yourResult:    'Your result',
  day:           'Day',
  naturalMedian: 'Natural median',
  hrtMedian:     'With HRT',
  yourZone:      'Your approx. position',
  stages: {
    earlyPeri: 'Early Peri',
    latePeri:  'Late Peri',
    menopause: 'Menopause',
    postmeno:  'Postmenopause',
  },
};

// ── Soft zone indicator (menopause transition) ────────────────────────────────

const SoftZoneDot = ({ cx, cy }) => (
  <g>
    <circle cx={cx} cy={cy} r={22} fill={tokens.teal} fillOpacity={0.07} />
    <circle cx={cx} cy={cy} r={14} fill={tokens.teal} fillOpacity={0.13} />
    <circle cx={cx} cy={cy} r={7}  fill={tokens.teal} fillOpacity={0.55} />
    <circle cx={cx} cy={cy} r={3}  fill={tokens.teal} />
  </g>
);

// ── Cycle chart ───────────────────────────────────────────────────────────────

const PHASE_ORDER = ['menstrual', 'follicular', 'ovulatory', 'luteal'];

function CycleChart({ markerId, result, cycleDay, bgColor, lang }) {
  const theme  = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const cfg    = MARKER_CHART[markerId];
  if (!cfg?.hasCycleCurve) return null;

  const currentValue = result != null ? Number(result.value) : null;

  // P4 curve data is stored in nmol/L; the lab result is reported in ng/mL — convert to match.
  const displayUnit = markerId === 'P4' ? 'ng/mL' : cfg.unit;
  const convert = markerId === 'P4' ? v => v / P4_NMOL_PER_NGML : v => v;

  const chartData = CYCLE_DATA.map(d => ({
    day:     d.day,
    mid:     convert(d[`${markerId}mid`]),
    low:     convert(d[`${markerId}low`]),
    high:    convert(d[`${markerId}high`]),
    current: cycleDay === d.day ? currentValue : null,
  }));

  // In light mode the bg palette colors are very pale pastels — raise opacity for visibility.
  // In dark mode switch to the saturated line colors at low opacity so they show against dark bg.
  const phaseOp    = isDark ? 0.22 : 0.85;
  const phaseFill  = (phase) => isDark ? PHASE_COLORS[phase].line : PHASE_COLORS[phase].bg;
  const gridStroke = isDark ? 'rgba(255,255,255,0.08)' : tokens.borderLight;
  const tickFill   = isDark ? 'rgba(255,255,255,0.45)' : tokens.textMuted;
  const tooltipBg  = isDark ? (theme.palette.greydark?.C900 ?? '#1c1a24') : '#fff';
  const tooltipBorder = isDark ? 'rgba(255,255,255,0.12)' : tokens.border;

  return (
    <>
      <ResponsiveContainer width="100%" height={200}>
      <ComposedChart data={chartData} margin={{ top: 24, right: 8, left: 0, bottom: 12 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />

        <ReferenceArea x1={1}  x2={5}  fill={phaseFill('menstrual')}  fillOpacity={phaseOp} />
        <ReferenceArea x1={6}  x2={12} fill={phaseFill('follicular')} fillOpacity={phaseOp} />
        <ReferenceArea x1={13} x2={15} fill={phaseFill('ovulatory')}  fillOpacity={phaseOp} />
        <ReferenceArea x1={16} x2={28} fill={phaseFill('luteal')}     fillOpacity={phaseOp} />

        <ReferenceLine x={14} stroke={tokens.teal} strokeDasharray="4 2" strokeWidth={1} />

        <Area type="monotone" dataKey="high" fill={cfg.color} stroke="none" fillOpacity={0.12} />
        <Area type="monotone" dataKey="low"  fill={bgColor}   stroke="none" fillOpacity={1} />
        <Line type="monotone" dataKey="mid"  stroke={cfg.color} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />

        <Line
          type="monotone"
          dataKey="current"
          stroke="none"
          dot={({ cx, cy, payload }) =>
            payload.current != null
              ? <circle key={`cd-${payload.day}`} cx={cx} cy={cy} r={7} fill={cfg.color} stroke={bgColor} strokeWidth={2} />
              : <g key={`cd-${payload.day}`} />
          }
          activeDot={false}
          isAnimationActive={false}
        />

        {cycleDay != null && (
          <ReferenceLine
            x={cycleDay}
            stroke={tickFill}
            strokeDasharray="2 2"
            label={{ value: `${CHART_COPY.day} ${cycleDay}`, position: 'top', fontSize: 11, fontWeight: 600, fill: tokens.teal }}
          />
        )}

        <XAxis
          dataKey="day"
          tick={{ fontSize: 9, fill: tickFill }}
          label={{ value: CHART_COPY.dayLabel, position: 'insideBottomRight', offset: -4, fontSize: 9, fill: tickFill }}
        />
        <YAxis
          tick={{ fontSize: 9, fill: tickFill }}
          width={26}
          tickFormatter={v => Number(v).toFixed(v < 10 ? 1 : 0)}
        />
        <RechartsTooltip
          contentStyle={{ fontSize: 11, borderRadius: 8, background: tooltipBg, border: `1px solid ${tooltipBorder}` }}
          labelStyle={{ color: tickFill }}
          itemStyle={{ color: tickFill }}
          formatter={(v, name) => [
            `${Number(v).toFixed(2)} ${displayUnit}`,
            name === 'mid' ? CHART_COPY.median : name === 'current' ? CHART_COPY.yourResult : name,
          ]}
          labelFormatter={day => `${CHART_COPY.day} ${day}`}
        />
      </ComposedChart>
    </ResponsiveContainer>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1.5, px: 0.5 }}>
        {PHASE_ORDER.map(phase => {
          const color = phaseFill(phase);
          const label = ht(lang, 'chart', 'phases', phase) ?? phase;
          return (
            <Box key={phase} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color, border: '1px solid', borderColor: tooltipBorder, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 500, color: tickFill }}>
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </>
  );
}

// ── Menopause transition chart ────────────────────────────────────────────────

function MenopauseChart({ markerId, result, fshValue, user, bgColor }) {
  const theme  = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const cfg    = MARKER_CHART[markerId];
  const curves = MENOPAUSE_CURVES[markerId];
  if (!cfg?.hasMenoCurve || !curves) return null;

  const showHRT = stateHasHRT(user.hormoneState) || user.onHRT;
  const dotX    = result != null ? getMenopausePosition(user.age, fshValue ?? null) : null;

  // P4 curve data is stored in nmol/L; the lab result is reported in ng/mL — convert to match.
  const displayUnit = markerId === 'P4' ? 'ng/mL' : curves.unit;
  const convert = markerId === 'P4' ? v => v / P4_NMOL_PER_NGML : v => v;

  const chartData = curves.natural.map((pt, i) => ({
    ...pt,
    mid: convert(pt.mid), low: convert(pt.low), high: convert(pt.high),
    ...(showHRT ? {
      hrtMid:  convert(curves.hrt[i].mid),
      hrtLow:  convert(curves.hrt[i].low),
      hrtHigh: convert(curves.hrt[i].high),
    } : {}),
  }));

  const dotData = (() => {
    if (dotX === null) return [];
    const fl = Math.floor(dotX);
    const fr = dotX - fl;
    const a  = curves.natural[Math.min(fl, 3)];
    const b  = curves.natural[Math.min(fl + 1, 3)];
    return [{ x: dotX, y: convert(a.mid + (b.mid - a.mid) * fr) }];
  })();

  const gridStroke    = isDark ? 'rgba(255,255,255,0.08)' : tokens.borderLight;
  const tickFill      = isDark ? 'rgba(255,255,255,0.45)' : tokens.textMuted;
  const tooltipBg     = isDark ? (theme.palette.greydark?.C900 ?? '#1c1a24') : '#fff';
  const tooltipBorder = isDark ? 'rgba(255,255,255,0.12)' : tokens.border;
  // HRT band: tealLight is near-white — use teal at low opacity in dark mode
  const hrtFill    = isDark ? tokens.teal : HRT_BAND.fill;
  const hrtOpacity = isDark ? 0.18 : HRT_BAND.opacity;

  return (
    <ResponsiveContainer width="100%" height={200}>
      <ComposedChart data={chartData} margin={{ top: 16, right: 8, left: 0, bottom: 12 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />

        <Area type="monotone" dataKey="high" fill={cfg.color} stroke="none" fillOpacity={0.12} />
        <Area type="monotone" dataKey="low"  fill={bgColor}   stroke="none" fillOpacity={1} />
        <Line type="monotone" dataKey="mid"  stroke={cfg.color} strokeWidth={2} dot={false} />

        {showHRT && <>
          <Area type="monotone" dataKey="hrtHigh" fill={hrtFill}  stroke="none" fillOpacity={hrtOpacity} />
          <Area type="monotone" dataKey="hrtLow"  fill={bgColor} stroke="none" fillOpacity={1} />
          <Line type="monotone" dataKey="hrtMid"  stroke={tokens.teal} strokeWidth={1.5} strokeDasharray="5 3" dot={false} />
        </>}

        {dotData.length > 0 && (
          <Scatter
            data={dotData}
            dataKey="y"
            shape={({ cx, cy }) => <SoftZoneDot cx={cx} cy={cy} />}
          />
        )}

        <XAxis
          dataKey="x"
          type="number"
          domain={[0, 3]}
          ticks={[0, 1, 2, 3]}
          tickFormatter={i => CHART_COPY.stages[MENOPAUSE_STAGES[i]?.labelKey] ?? ''}
          tick={{ fontSize: 9, fill: tickFill }}
          interval={0}
        />
        <YAxis
          tick={{ fontSize: 9, fill: tickFill }}
          width={26}
          tickFormatter={v => Number(v).toFixed(v < 10 ? 1 : 0)}
        />
        <RechartsTooltip
          contentStyle={{ fontSize: 11, borderRadius: 8, background: tooltipBg, border: `1px solid ${tooltipBorder}` }}
          labelStyle={{ color: tickFill }}
          itemStyle={{ color: tickFill }}
          formatter={(v, name) => [
            `${Number(v).toFixed(2)} ${displayUnit}`,
            name === 'mid'      ? CHART_COPY.naturalMedian
            : name === 'hrtMid' ? CHART_COPY.hrtMedian
            : name === 'y'      ? CHART_COPY.yourZone
            : name,
          ]}
          labelFormatter={x => {
            const idx = Math.round(x);
            return CHART_COPY.stages[MENOPAUSE_STAGES[idx]?.labelKey] ?? '';
          }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// ── GaugeBar (mirrored from HormoneDetailsFemale) ────────────────────────────

function GaugeBar({ value, low, high }) {
  const theme = useTheme();
  if (value == null || (low == null && high == null)) return null;

  const val = Number(value);
  const lo  = low  != null ? Number(low)  : null;
  const hi  = high != null ? Number(high) : null;

  let dMin, dMax;
  if (lo != null && hi != null) {
    const range = (hi - lo) || 1;
    dMin = lo - range * 0.5;
    dMax = hi + range * 0.5;
  } else if (lo != null) {
    dMin = Math.min(0, lo * 0.5);
    dMax = Math.max(lo * 2, val * 1.2);
  } else {
    dMin = Math.min(0, val * 0.5);
    dMax = Math.max(hi * 1.5, val * 1.2);
  }

  const pad = (dMax - dMin) * 0.05 || 1;
  dMin = Math.min(dMin, val - pad);
  dMax = Math.max(dMax, val + pad);

  const span    = dMax - dMin;
  const clamp   = v => Math.max(0, Math.min(100, ((v - dMin) / span) * 100));
  const lowPct  = lo != null ? clamp(lo) : 0;
  const highPct = hi != null ? clamp(hi) : 100;
  const valPct  = clamp(val);
  const isNormal = (lo == null || val >= lo) && (hi == null || val <= hi);

  return (
    <Box sx={{ position: 'relative', width: '100%', pb: 1 }}>
      <Box sx={{ position: 'relative', width: '100%', height: 5, bgcolor: 'action.disabledBackground', borderRadius: 0.5 }}>
        <Box sx={{ position: 'absolute', left: `${lowPct}%`, width: `${highPct - lowPct}%`, height: '100%', bgcolor: theme.palette.traffic.green, opacity: 0.4 }} />
        <Box sx={{ position: 'absolute', top: '50%', left: `${valPct}%`, transform: 'translate(-50%, -50%)', width: 4, height: 14, bgcolor: isNormal ? theme.palette.traffic.green : theme.palette.traffic.red }} />
      </Box>
      {lo != null && (
        <Typography variant="caption" color="text.disabled" sx={{ position: 'absolute', top: '100%', left: `${lowPct}%`, transform: 'translateX(-50%)', fontSize: '0.6rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {lo.toFixed(2)}
        </Typography>
      )}
      {hi != null && (
        <Typography variant="caption" color="text.disabled" sx={{ position: 'absolute', top: '100%', left: `${highPct}%`, transform: 'translateX(-50%)', fontSize: '0.6rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {hi.toFixed(2)}
        </Typography>
      )}
    </Box>
  );
}

// ── Across-lifespan context box ───────────────────────────────────────────────

function LifespanContext({ markerId, hormoneState, lang, markerColor, aiSummary, chips = [] }) {
  const theme = useTheme();
  const title   = ht(lang, 'markerSection', 'assessmentLabel');
  const staticText = ht(lang, 'markers', markerId, 'acrossLifespan', hormoneState);
  const displayText = aiSummary || staticText;

  if (!displayText) return null;

  return (
    <Box sx={{
      mt: 1.5, p: 1.5,
      borderRadius: 2,
      overflow: 'hidden',
      borderLeft: `3px solid ${markerColor}`,
      bgcolor: theme.palette.mode === 'light' ? alpha('#fff', 0.6) : alpha('#fff', 0.03),
      backdropFilter: 'blur(16px) saturate(160%)',
      WebkitBackdropFilter: 'blur(16px) saturate(160%)',
      border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.06)'}`,
      boxShadow: theme.palette.mode === 'light'
        ? 'inset 0 1px 0 rgba(255,255,255,0.6), 0 2px 12px rgba(0,0,0,0.04)'
        : 'inset 0 1px 0 rgba(255,255,255,0.04), 0 4px 16px rgba(0,0,0,0.4)',
    }}>
      {title && (
        <Typography variant="caption" fontWeight={700} sx={{
          textTransform: 'uppercase', letterSpacing: 0.5,
          color: 'text.secondary', display: 'block',
          fontSize: '0.6rem', mb: 0.5,
        }}>
          {title}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.65, display: 'block', fontSize: '0.75rem', whiteSpace: 'pre-line' }}>
        {displayText}
      </Typography>
      {chips.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
          {chips.map(chip => (
            <Chip
              key={chip}
              label={ht(lang, 'bodyContext', 'tabs', chip) ?? chip}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

// ── Collapsible accordion (shared by symptom connections + context notes) ────

function ContextAccordion({ title, accentColor, children }) {
  const [open, setOpen] = useState(false);
  if (!children) return null;
  return (
    <Box sx={{ mt: 1.5, borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}>
      <Box
        component="button"
        onClick={() => setOpen(o => !o)}
        sx={{
          width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'none', border: 'none', cursor: 'pointer', p: 0, fontFamily: 'inherit',
        }}
      >
        <Typography variant="caption" fontWeight={700} sx={{ color: accentColor, fontSize: '0.75rem', textAlign: 'left' }}>
          {title}
        </Typography>
        <ChevronDown
          size={14}
          color={accentColor}
          style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
        />
      </Box>
      {open && <Box sx={{ mt: 1.25 }}>{children}</Box>}
    </Box>
  );
}

// ── Symptom connections ───────────────────────────────────────────────────────

const SYMS = ['cycle', 'fertility', 'libido', 'mood', 'energy'];

function SymptomConnections({ markerId, markerColor, lang }) {
  const rows = SYMS
    .map(sym => ({
      sym,
      label: ht(lang, 'markerSection', 'symptoms', sym),
      text: ht(lang, 'markers', markerId, 'symptomConnections', sym),
    }))
    .filter(r => r.text);

  if (!rows.length) return null;

  const title = ht(lang, 'markerSection', 'symptomConnections') ?? 'Symptom connections';

  return (
    <ContextAccordion title={title} accentColor={markerColor}>
      {rows.map(({ sym, label, text }) => (
        <Box key={sym} sx={{ display: 'flex', gap: 1, mb: 1, '&:last-child': { mb: 0 } }}>
          <Typography variant="caption" fontWeight={700} sx={{
            textTransform: 'uppercase', letterSpacing: '0.05em',
            color: markerColor, flexShrink: 0, pt: 0.25, minWidth: 68, fontSize: '0.6rem',
          }}>
            {label ?? sym}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: '0.75rem' }}>
            {text}
          </Typography>
        </Box>
      ))}
    </ContextAccordion>
  );
}

// ── Single context note (fertility / contraception / HRT) ─────────────────────

function ContextNote({ title, text, accentColor }) {
  if (!text) return null;
  return (
    <ContextAccordion title={title} accentColor={accentColor}>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.65, display: 'block', fontSize: '0.75rem' }}>
        {text}
      </Typography>
    </ContextAccordion>
  );
}

// ── Body context tabs (mood / energy / libido / pain) ────────────────────────

function BodyContextTabs({ hormoneState, cyclePhase, lang }) {
  const [activeTab, setActiveTab] = useState('mood');
  const theme = useTheme();
  const tabs = ['mood', 'energy', 'libido', 'pain'];

  const isCycle = ['natural_cycle', 'hormonal_contraception'].includes(hormoneState);
  const phase   = cyclePhase || 'follicular';
  const stage   = hormoneState === 'perimenopause' ? 'earlyPeri' : 'menopause';

  const text = isCycle
    ? ht(lang, 'bodyContext', 'phases', phase, activeTab)
    : ht(lang, 'bodyContext', 'stages', stage, activeTab);

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
        {tabs.map(tab => {
          const label = ht(lang, 'bodyContext', 'tabs', tab);
          const isActive = activeTab === tab;
          return (
            <Box
              key={tab}
              component="button"
              onClick={() => setActiveTab(tab)}
              sx={{
                px: 1.5, py: 0.5,
                borderRadius: 99,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: isActive ? tokens.teal : 'divider',
                bgcolor: isActive
                  ? (theme.palette.mode === 'light' ? `${tokens.teal}18` : `${tokens.teal}28`)
                  : 'transparent',
                color: isActive ? tokens.teal : 'text.secondary',
                fontSize: '0.75rem',
                fontWeight: isActive ? 600 : 400,
                fontFamily: 'inherit',
                lineHeight: 1.5,
                outline: 'none',
                transition: 'all 0.15s',
                '&:hover': { borderColor: tokens.teal },
              }}
            >
              {label}
            </Box>
          );
        })}
      </Box>
      {text && (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.65, display: 'block', fontSize: '0.8rem' }}>
          {text}
        </Typography>
      )}
    </Box>
  );
}

// ── Exported panel ────────────────────────────────────────────────────────────

export default function HormoneChartPanel({
  femaleProfile,
  cycleDay,
  cyclePhase,
  hormoneResults,
  userAge,
  language = 'en',
  selectedMarkerId = 'LH',
  onOpenChart,
  onOpenInfo,
  t,
  aiSummary = null,
}) {
  const theme = useTheme();

  if (!femaleProfile) return null;

  const hormoneState  = toHormoneState(femaleProfile.profile, femaleProfile.status);
  const mode          = STATE_CHART_MODE[hormoneState];
  const useCycleChart = mode === 'cycle';

  const user = {
    age:            userAge ?? 35,
    hormoneState,
    onHRT:          femaleProfile.status === 'hrt',
    irregularCycles: femaleProfile.profile === 'perimenopause',
  };

  const bgColor = theme.palette.mode === 'light'
    ? (theme.palette.grey?.C50 ?? '#fafafa')
    : (theme.palette.greydark?.C980 ?? theme.palette.background.paper);

  const cardSx = {
    borderRadius: 4,
    overflow: 'hidden',
    bgcolor: theme.palette.mode === 'dark' ? alpha('#fff', 0.04) : alpha('#fff', 0.5),
    backdropFilter: 'blur(28px) saturate(180%)',
    WebkitBackdropFilter: 'blur(28px) saturate(180%)',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.65)'}`,
    boxShadow: theme.palette.mode === 'dark'
      ? 'inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)'
      : 'inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)',
  };

  const fshResult = findResult(hormoneResults, 'FSH');
  const fshValue  = fshResult ? parseFloat(fshResult.value) : null;

  const markerId    = selectedMarkerId;
  const result      = findResult(hormoneResults, markerId);
  const cfg         = MARKER_CHART[markerId];
  const lang        = language === 'de' ? 'de' : 'en';

  const markerShort = result?.marker_short ?? MARKER_SHORT_FALLBACK[markerId];
  const markerName  = t ? t(`markername_${markerShort}`) : markerId;
  const markerDescr = t ? t(`markerdescr_${markerShort}`) : null;

  const statusKey  = `bt_status_${(result?.status || '').toLowerCase().replace(/\s+/g, '_')}`;
  const statusText = t && t(statusKey) !== statusKey ? t(statusKey) : (result?.status ?? '');

  const hasChart = useCycleChart
    ? !!cfg?.hasCycleCurve
    : !!(cfg?.hasMenoCurve && MENOPAUSE_CURVES[markerId]);

  return (
    <Box sx={cardSx}>
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2.5, py: 1.75, gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
        <Box sx={{ flexGrow: 1, minWidth: 120, order: { xs: 1, sm: 1 }, flexBasis: { xs: '100%', sm: 'auto' } }}>
          <Typography variant="body1" fontWeight={500}>{markerName}</Typography>
          <Typography variant="caption" color="text.secondary">{markerDescr}</Typography>
        </Box>
        <Typography variant="number3" fontWeight={400} sx={{ minWidth: 80, textAlign: 'right', fontVariantNumeric: 'tabular-nums', order: { xs: 2, sm: 2 } }}>
          {result?.value != null ? Number(result.value).toFixed(2) : '—'}{result?.unit ? ` ${result.unit}` : ''}
        </Typography>
        <Box sx={{ minWidth: 68, display: 'flex', justifyContent: 'flex-end', flexShrink: 0, order: { xs: 3, sm: 4 } }}>
          {result?.status?.toUpperCase() === 'NORMAL'
            ? <Check size={18} strokeWidth={3} color={theme.palette.traffic.green} />
            : <Typography variant="subtitle2" fontWeight={700} sx={{ color: statusColor(theme, result?.status) }}>{statusText}</Typography>
          }
        </Box>
        <Tooltip title="Info" placement="top">
          <IconButton size="small" onClick={() => onOpenInfo?.(markerShort)} sx={{ order: { xs: 4, sm: 5 }, '&:hover': { transform: 'scale(1.2)' } }}>
            <Info size={18} color={theme.palette.secondary.main} />
          </IconButton>
        </Tooltip>
        <Tooltip title={t ? t('bt_chart_history') : 'History'} placement="top">
          <IconButton size="small" onClick={() => onOpenChart?.(markerShort)} sx={{ order: { xs: 5, sm: 6 }, '&:hover': { transform: 'scale(1.2)' } }}>
            <ChartNoAxesCombined size={18} color={theme.palette.primary.main} />
          </IconButton>
        </Tooltip>
        <Box sx={{
          minWidth: 140, ml: { xs: 0, sm: 2 }, display: 'flex', alignItems: 'center',
          order: { xs: 6, sm: 3 }, flexBasis: { xs: '100%', sm: 'auto' }, mt: { xs: 0.5, sm: 0 }, mb: { xs: 1.5, sm: 0 },
        }}>
          <Box sx={{ width: { xs: '60%', sm: '100%' } }}>
            <GaugeBar value={result?.value} low={result?.ref_low} high={result?.ref_high} />
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mx: 2.5 }} />

      {/* Chart */}
      {hasChart && (
        <Box sx={{ px: 2.5, pt: 3, pb: 1.5 }}>
          {useCycleChart ? (
            <CycleChart markerId={markerId} result={result} cycleDay={cycleDay} bgColor={bgColor} lang={lang} />
          ) : (
            <MenopauseChart markerId={markerId} result={result} fshValue={fshValue} user={user} bgColor={bgColor} />
          )}
        </Box>
      )}

      {/* Context sections */}
      <Box sx={{ px: 2.5, pb: 2.5 }}>
        <LifespanContext markerId={markerId} hormoneState={hormoneState} lang={lang} markerColor={cfg?.color ?? tokens.teal} aiSummary={aiSummary} chips={MARKER_CHIPS[markerId] ?? []} />
      </Box>
    </Box>
  );
}
